import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $getNodeByKey,
  FORMAT_TEXT_COMMAND,
} from "lexical";
import { $generateNodesFromDOM } from "@lexical/html";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $generateHtmlFromNodes } from "@lexical/html";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import {
  toggleBold,
  toggleItalic,
  toggleUnderline,
  toggleHighlight,
  applyTextColor,
  applyFontSize,
  getFormattingState,
  applyMultipleFormats,
  applyBoldAndItalic,
} from "./LexicalFormattingUtils";
import generateHtmlWithStylesFromNodes from "../canvas-core/generateHtmlWithStylesFromNodes";
import { isLightColor } from "../text/utils/colorUtils";

// Plugin to make light-colored text visible on white backgrounds
function LightTextVisibilityPlugin({ editorId }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    let updateTimeout = null;
    const LIGHT_TEXT_SHADOW = 
      "1px 1px 2px rgba(0, 0, 0, 0.8), " +
      "-1px -1px 2px rgba(0, 0, 0, 0.8), " +
      "1px -1px 2px rgba(0, 0, 0, 0.8), " +
      "-1px 1px 2px rgba(0, 0, 0, 0.8), " +
      "0 0 3px rgba(0, 0, 0, 0.9)";

    const updateLightTextStyles = () => {
      const editorElement = editor.getRootElement();
      if (!editorElement) return;

      // Find all elements that might have text content
      // Focus on elements with inline styles or that contain text nodes
      const allElements = editorElement.querySelectorAll("*");
      const processedElements = new Set();
      
      allElements.forEach((element) => {
        // Skip if already processed
        if (processedElements.has(element)) return;
        
        // Check if element has text content or is a text node
        const hasText = element.textContent && element.textContent.trim().length > 0;
        if (!hasText && element.children.length === 0) return;
        
        const computedStyle = window.getComputedStyle(element);
        const color = computedStyle.color;
        
        // Check if color is light
        if (color && isLightColor(color)) {
          // Check if shadow is already applied (to avoid re-applying)
          const currentTextShadow = element.style.textShadow || computedStyle.textShadow;
          const hasShadow = currentTextShadow && 
            currentTextShadow !== "none" && 
            currentTextShadow.includes("rgba(0, 0, 0");
          
          if (!hasShadow) {
            // Apply a dark text shadow to make light text visible on white background
            element.style.textShadow = LIGHT_TEXT_SHADOW;
            processedElements.add(element);
          }
        } else {
          // If color is not light, remove shadow if it was previously applied
          const currentTextShadow = element.style.textShadow || computedStyle.textShadow;
          if (currentTextShadow && currentTextShadow.includes("rgba(0, 0, 0")) {
            // Only remove if it's our shadow (check for the specific pattern)
            if (currentTextShadow === LIGHT_TEXT_SHADOW || 
                currentTextShadow.includes("1px 1px 2px rgba(0, 0, 0, 0.8)")) {
              element.style.textShadow = "";
            }
          }
        }
      });
    };

    // Debounced update function
    const debouncedUpdate = () => {
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      updateTimeout = setTimeout(updateLightTextStyles, 50);
    };

    // Update styles on editor updates
    const unregister = editor.registerUpdateListener(() => {
      debouncedUpdate();
    });

    // Initial update
    setTimeout(updateLightTextStyles, 100);

    // Also use MutationObserver for DOM changes
    const editorElement = editor.getRootElement();
    if (editorElement) {
      const observer = new MutationObserver(() => {
        debouncedUpdate();
      });

      observer.observe(editorElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style"],
      });

      return () => {
        if (updateTimeout) {
          clearTimeout(updateTimeout);
        }
        unregister();
        observer.disconnect();
      };
    }

    return () => {
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      unregister();
    };
  }, [editor, editorId]);

  return null;
}

// Simple Lexical Text Editor Component
function SimpleLexicalEditor({
  value = "",
  onChange,
  placeholder = "Enter text...",
  style = {},
  onFocus,
  onBlur,
  showToolbar = false, // Default to false since we're using existing UI
  autoFocus = false,
}) {
  const [editor] = useLexicalComposerContext();
  const [formattingState, setFormattingState] = useState({
    isBold: false,
    isItalic: false,
    isUnderline: false,
    isHighlighted: false,
    textColor: "#000000",
  });

  // Store the initialized value to track changes
  const initializedValueRef = useRef(null);
  // Track if the incoming value change originated from this editor (typing/formatting)
  const isInternalUpdateRef = useRef(false);
  // Store the style prop so increment/decrement functions can access the default font size
  const styleRef = useRef(style);

  // Update styleRef when style changes
  useEffect(() => {
    styleRef.current = style;
  }, [style]);

  // Initialize content with style preservation
  useEffect(() => {
    if (!editor || value === undefined) return;

    // If this change came from our own onChange, don't reinitialize the editor
    if (isInternalUpdateRef.current) {
      initializedValueRef.current = value;
      isInternalUpdateRef.current = false;
      return;
    }

    if (value !== initializedValueRef.current) {
      const previousValue = initializedValueRef.current;
      initializedValueRef.current = value;

      // Check if the new value contains highlights
      const hasHighlight =
        value &&
        (value.includes("background-color") ||
          value.includes("yellow") ||
          value.includes("#ffff00") ||
          value.includes("rgb(255, 255, 0)"));

      editor.update(() => {
        const root = $getRoot();
        root.clear();

        // If we have content, try to parse it safely
        if (value && value.trim() !== "") {
          try {
            // Create temporary element to hold and parse the HTML
            const tempContainer = document.createElement("div");
            // Convert <br> tags to paragraph breaks before parsing
            // This ensures line breaks are properly represented as separate paragraphs
            const contentWithParagraphs = value.trim().split(/<br\s*\/?>/i);

            // Process each line as a separate paragraph
            contentWithParagraphs.forEach((lineContent, index) => {
              if (
                !lineContent.trim() &&
                index === contentWithParagraphs.length - 1
              ) {
                // Skip empty last line
                return;
              }

              const paragraph = $createParagraphNode();
              tempContainer.innerHTML = lineContent.trim();

              // Function to recursively parse elements and preserve styling
              // Pass inherited styles down the tree to handle nested elements
              const parseElementWithStyles = (
                element,
                inheritedStyles = {}
              ) => {
                if (element.nodeType === Node.TEXT_NODE) {
                  const textContent = element.textContent;
                  // Preserve whitespace-only text nodes to keep spacing between inline elements
                  if (textContent !== null && textContent !== undefined) {
                    const textNode = $createTextNode(textContent);
                    // Apply any inherited styles to the text node
                    if (Object.keys(inheritedStyles).length > 0) {
                      const styleString = Object.entries(inheritedStyles)
                        .map(([k, v]) => `${k}: ${v}`)
                        .join("; ");
                      textNode.setStyle(styleString);
                    }
                    return textNode;
                  }
                  return null;
                } else if (element.nodeType === Node.ELEMENT_NODE) {
                  const tagName = element.tagName.toLowerCase();
                  const styleAttr = element.getAttribute("style") || "";

                  // Parse style attribute into an object
                  const currentStyles = { ...inheritedStyles };
                  if (styleAttr) {
                    styleAttr.split(";").forEach((s) => {
                      const idx = s.indexOf(":");
                      if (idx > -1) {
                        const key = s.slice(0, idx).trim().toLowerCase();
                        const value = s.slice(idx + 1).trim();
                        if (key && value) {
                          currentStyles[key] = value;
                        }
                      }
                    });
                  }

                  let textNodes = [];

                  // Process child nodes, passing down the accumulated styles
                  for (let child of element.childNodes) {
                    const childResult = parseElementWithStyles(
                      child,
                      currentStyles
                    );
                    if (childResult) {
                      if (Array.isArray(childResult)) {
                        textNodes.push(...childResult);
                      } else {
                        textNodes.push(childResult);
                      }
                    }
                  }

                  // Apply formatting based on element type
                  textNodes.forEach((textNode) => {
                    if (
                      textNode &&
                      typeof textNode.getType === "function" &&
                      textNode.getType() === "text"
                    ) {
                      // Apply semantic formatting
                      if (tagName === "strong" || tagName === "b") {
                        textNode.toggleFormat("bold");
                      }
                      if (tagName === "em" || tagName === "i") {
                        textNode.toggleFormat("italic");
                      }
                      if (tagName === "u") {
                        textNode.toggleFormat("underline");
                      }
                      if (
                        tagName === "s" ||
                        tagName === "strike" ||
                        tagName === "del"
                      ) {
                        textNode.toggleFormat("strikethrough");
                      }

                      // Apply/merge inline styles - styles are already applied during recursion
                      // but ensure they're set correctly (in case element had no children)
                      const existingStyle = textNode.getStyle() || "";
                      if (Object.keys(currentStyles).length > 0) {
                        const styleString = Object.entries(currentStyles)
                          .map(([k, v]) => `${k}: ${v}`)
                          .join("; ");
                        if (existingStyle) {
                          // Merge with existing
                          const existingMap = {};
                          existingStyle.split(";").forEach((s) => {
                            const idx = s.indexOf(":");
                            if (idx > -1) {
                              const key = s.slice(0, idx).trim().toLowerCase();
                              const value = s.slice(idx + 1).trim();
                              if (key && value) existingMap[key] = value;
                            }
                          });
                          Object.entries(currentStyles).forEach(([k, v]) => {
                            existingMap[k.toLowerCase()] = v;
                          });
                          const merged = Object.entries(existingMap)
                            .map(([k, v]) => `${k}: ${v}`)
                            .join("; ");
                          textNode.setStyle(merged);
                        } else {
                          textNode.setStyle(styleString);
                        }
                      }
                    }
                  });

                  return textNodes;
                }
                return null;
              };

              // Parse all child elements
              for (let child of tempContainer.childNodes) {
                const result = parseElementWithStyles(child);
                if (result) {
                  if (Array.isArray(result)) {
                    result.forEach((node) => {
                      if (node) {
                        // Debug: log if node has background-color
                        if (typeof node.getStyle === "function") {
                          const style = node.getStyle();
                          if (style && style.includes("background-color")) {
                          }
                        }
                        paragraph.append(node);
                      }
                    });
                  } else {
                    // Debug: log if node has background-color
                    if (result && typeof result.getStyle === "function") {
                      const style = result.getStyle();
                      if (style && style.includes("background-color")) {
                      }
                    }
                    paragraph.append(result);
                  }
                }
              }

              // Ensure paragraph has content (even if empty, to preserve line breaks)
              if (
                paragraph.getTextContent() === "" &&
                lineContent.trim() === ""
              ) {
                paragraph.append($createTextNode(""));
              } else if (paragraph.getTextContent() === "") {
                paragraph.append($createTextNode(""));
              }

              root.append(paragraph);
            });
          } catch (error) {
            // Final fallback: create paragraph with plain text
            const paragraph = $createParagraphNode();
            const textNode = $createTextNode(value.replace(/<[^>]*>/g, ""));
            paragraph.append(textNode);
            root.append(paragraph);
          }
        } else {
          // Create a default paragraph node for empty content
          const paragraph = $createParagraphNode();
          root.append(paragraph);
        }
      });
    }
  }, [editor, value]);

  // Handle editor changes
  const handleEditorChange = useCallback(
    (editorState) => {
      if (onChange) {
        editorState.read(() => {
          // Use the same HTML generation as the main LexicalTextEditor to preserve styles
          let content = generateHtmlWithStylesFromNodes(editor);

          // Sanitize styles: if a node has both color and background-color equal, drop color
          const sanitizeInlineStyles = (html) => {
            try {
              const container = document.createElement("div");
              container.innerHTML = html;
              const styledNodes = container.querySelectorAll("[style]");
              styledNodes.forEach((el) => {
                const styleAttr = el.getAttribute("style");
                if (!styleAttr) return;

                // Parse style string into a map
                const styleMap = {};
                styleAttr
                  .split(";")
                  .map((s) => s.trim())
                  .filter(Boolean)
                  .forEach((pair) => {
                    const idx = pair.indexOf(":");
                    if (idx > -1) {
                      const key = pair.slice(0, idx).trim().toLowerCase();
                      const value = pair.slice(idx + 1).trim();
                      styleMap[key] = value;
                    }
                  });

                const normalizeColor = (c) => {
                  if (!c) return null;
                  let v = String(c).trim().toLowerCase();
                  if (v === "yellow") return "rgb(255,255,0)";
                  if (v === "#ff0") return "#ffff00";
                  if (/^#([0-9a-f]{6})$/i.test(v)) {
                    const r = parseInt(v.slice(1, 3), 16);
                    const g = parseInt(v.slice(3, 5), 16);
                    const b = parseInt(v.slice(5, 7), 16);
                    return `rgb(${r},${g},${b})`;
                  }
                  if (/^#([0-9a-f]{3})$/i.test(v)) {
                    const r = parseInt(v[1] + v[1], 16);
                    const g = parseInt(v[2] + v[2], 16);
                    const b = parseInt(v[3] + v[3], 16);
                    return `rgb(${r},${g},${b})`;
                  }
                  const m = v.match(/^rgba?\(([^)]+)\)$/);
                  if (m) {
                    const parts = m[1]
                      .split(",")
                      .map((p) => p.trim())
                      .slice(0, 3)
                      .map((n) => parseInt(n, 10));
                    if (parts.length === 3 && parts.every((n) => !isNaN(n))) {
                      return `rgb(${parts[0]},${parts[1]},${parts[2]})`;
                    }
                  }
                  return v.replace(/\s+/g, "");
                };

                // If color equals background-color after normalization, drop color
                try {
                  // Instead of normalizing colors, compare the original values directly
                  // This prevents false matches when different hex colors normalize to similar RGB values
                  const originalColor = styleMap["color"];
                  const originalBgColor = styleMap["background-color"];

                  // Only remove color if the original values are exactly the same
                  if (
                    originalColor &&
                    originalBgColor &&
                    originalColor.toLowerCase().replace(/\s+/g, "") ===
                      originalBgColor.toLowerCase().replace(/\s+/g, "")
                  ) {
                    delete styleMap["color"];
                  } else {
                  }
                } catch (error) {
                  // Don't remove color on error - preserve it
                }

                // Rebuild style string
                const rebuilt = Object.entries(styleMap)
                  .map(([k, v]) => `${k}: ${v}`)
                  .join("; ");

                if (rebuilt) {
                  el.setAttribute("style", rebuilt);
                } else {
                  el.removeAttribute("style");
                }
              });
              return container.innerHTML;
            } catch (e) {
              return html;
            }
          };

          content = sanitizeInlineStyles(content);
          // Convert paragraph tags to <br> tags to preserve line breaks
          // First, replace closing </p> tags with <br> to create line breaks between paragraphs
          content = content.replace(/<\/p>/g, "<br>");
          // Then remove opening <p> tags
          content = content.replace(/<p[^>]*>/g, "");
          // Clean up multiple consecutive <br> tags (but keep at least one for intentional breaks)
          content = content.replace(/(<br>\s*){3,}/g, "<br><br>");
          // Remove leading <br> tag if present
          content = content.replace(/^<br>/, "");
          // Remove trailing <br> tag if present
          content = content.replace(/<br>$/, "");

          // Mark this change as internal so parent prop update doesn't reset selection
          isInternalUpdateRef.current = true;
          onChange(content);
        });
      }
    },
    [onChange, editor]
  );

  // Update formatting states
  useEffect(() => {
    const unregisterListener = editor.registerUpdateListener(
      ({ editorState }) => {
        editorState.read(() => {
          const newState = getFormattingState(editor);
          setFormattingState(newState);
          // Dispatch event to notify parent components of editor updates
          try {
            window.dispatchEvent(new Event("lexical-editor-update"));
          } catch {}
        });
      }
    );

    return () => {
      unregisterListener();
    };
  }, [editor]);

  // Track text selection and expose to window
  useEffect(() => {
    if (editor) {
      const updateSelection = () => {
        editor.getEditorState().read(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection) && !selection.isCollapsed()) {
            const selectedText = selection.getTextContent();
            if (selectedText && selectedText.length > 0) {
              const rootElement = editor.getRootElement();
              window.selectedTextInfo = {
                text: selectedText,
                start: 0,
                end: selectedText.length,
                element: rootElement,
              };
            } else {
              window.selectedTextInfo = null;
            }

            // Compute font-size set across selected text nodes
            try {
              const defaultFontSizePx = (() => {
                const v = style?.fontSize || "14px";
                const m = String(v).match(/(\d+(?:\.\d+)?)px/);
                return m ? parseInt(m[1], 10) : 14;
              })();
              const nodes = selection.getNodes();
              const sizes = new Set();
              const colors = new Set();

              // Also check DOM for styles since getStyle may not be available
              try {
                const sel = window.getSelection && window.getSelection();
                if (sel && sel.rangeCount > 0) {
                  const range = sel.getRangeAt(0);

                  // Check all nodes in the range, not just the container
                  const walker = document.createTreeWalker(
                    range.commonAncestorContainer,
                    NodeFilter.SHOW_ELEMENT,
                    {
                      acceptNode: (node) => {
                        if (range.intersectsNode(node)) {
                          return NodeFilter.FILTER_ACCEPT;
                        }
                        return NodeFilter.FILTER_REJECT;
                      },
                    }
                  );

                  let node = walker.nextNode();
                  while (node) {
                    const withinEditor = node.closest(
                      '[contenteditable="true"]'
                    );
                    if (withinEditor && node.nodeType === Node.ELEMENT_NODE) {
                      // Check inline style
                      const inlineStyle = node.getAttribute("style");
                      if (inlineStyle) {
                        // Check for color in inline style
                        const colorMatch =
                          inlineStyle.match(/color:\s*([^;]+)/);
                        if (colorMatch && colorMatch[1]) {
                          colors.add(colorMatch[1].trim());
                        }

                        // Check for font-size in inline style
                        const sizeMatch =
                          inlineStyle.match(/font-size:\s*([^;]+)/);
                        if (sizeMatch && sizeMatch[1]) {
                          const sizeStr = sizeMatch[1].trim();
                          const pxMatch = sizeStr.match(/(\d+(?:\.\d+)?)px/);
                          if (pxMatch) {
                            sizes.add(parseInt(pxMatch[1], 10));
                          } else {
                            const num = parseInt(sizeStr, 10);
                            if (!isNaN(num)) sizes.add(num);
                          }
                        }
                      }

                      // Also check computed style as fallback
                      const computedStyle = window.getComputedStyle(node);
                      const fontSize = computedStyle.fontSize;
                      if (fontSize) {
                        const pxMatch = fontSize.match(/(\d+(?:\.\d+)?)px/);
                        if (pxMatch) {
                          sizes.add(parseInt(pxMatch[1], 10));
                        }
                      }

                      const color = computedStyle.color;
                      if (
                        color &&
                        color !== "rgb(0, 0, 0)" &&
                        color !== "rgba(0, 0, 0)"
                      ) {
                        colors.add(color);
                      }
                    }
                    node = walker.nextNode();
                  }

                  // Also check the container itself
                  let element =
                    range.commonAncestorContainer.nodeType === Node.TEXT_NODE
                      ? range.commonAncestorContainer.parentElement
                      : range.commonAncestorContainer;

                  // Walk up to find elements with inline styles
                  while (element && element.nodeType === Node.ELEMENT_NODE) {
                    const withinEditor = element.closest(
                      '[contenteditable="true"]'
                    );
                    if (!withinEditor) break;

                    const inlineStyle = element.getAttribute("style");
                    if (inlineStyle) {
                      // Check for color in inline style
                      const colorMatch = inlineStyle.match(/color:\s*([^;]+)/);
                      if (colorMatch && colorMatch[1]) {
                        colors.add(colorMatch[1].trim());
                      }

                      // Check for font-size in inline style
                      const sizeMatch =
                        inlineStyle.match(/font-size:\s*([^;]+)/);
                      if (sizeMatch && sizeMatch[1]) {
                        const sizeStr = sizeMatch[1].trim();
                        const pxMatch = sizeStr.match(/(\d+(?:\.\d+)?)px/);
                        if (pxMatch) {
                          sizes.add(parseInt(pxMatch[1], 10));
                        } else {
                          const num = parseInt(sizeStr, 10);
                          if (!isNaN(num)) sizes.add(num);
                        }
                      }
                    }
                    element = element.parentElement;
                  }
                }
              } catch (e) {
                // Silently handle errors
              }

              nodes.forEach((n) => {
                if (typeof n.getType === "function" && n.getType() === "text") {
                  const s =
                    typeof n.getStyle === "function" ? n.getStyle() : "";

                  // Extract font-size from node.getStyle() if available
                  const m = s && s.match(/font-size:\s*([^;]+)/);
                  if (m && m[1]) {
                    const px = String(m[1]).match(/(\d+(?:\.\d+)?)px/);
                    if (px) {
                      sizes.add(parseInt(px[1], 10));
                    } else {
                      const num = parseInt(m[1], 10);
                      if (!isNaN(num)) sizes.add(num);
                      else sizes.add(defaultFontSizePx);
                    }
                  } else {
                    sizes.add(defaultFontSizePx);
                  }

                  // Extract color from node.getStyle() if available
                  const colorMatch = s && s.match(/color:\s*([^;]+)/);
                  if (colorMatch && colorMatch[1]) {
                    colors.add(colorMatch[1].trim());
                  }
                }
              });
              const unique = Array.from(sizes);
              const uniqueColors = Array.from(colors);
              const info = {
                sizes: unique,
                isMixed: unique.length > 1,
                max: unique.length > 0 ? Math.max(...unique) : null,
              };
              window.lexicalSelectedFontInfo = info;

              // Store color info for easy access
              if (uniqueColors.length > 0) {
                // Prefer hex colors over rgb, and prefer non-black colors
                const hexColors = uniqueColors.filter((c) => c.startsWith("#"));
                const nonBlackColors = uniqueColors.filter(
                  (c) =>
                    c !== "rgb(0, 0, 0)" &&
                    c !== "rgba(0, 0, 0)" &&
                    c !== "#000000" &&
                    c !== "black"
                );

                // Choose best color: prefer non-black hex, then any hex, then non-black rgb, then first
                let bestColor = uniqueColors[0];
                if (nonBlackColors.length > 0) {
                  // Prefer non-black colors
                  const nonBlackHexColors = nonBlackColors.filter((c) =>
                    c.startsWith("#")
                  );
                  bestColor =
                    nonBlackHexColors.length > 0
                      ? nonBlackHexColors[0]
                      : nonBlackColors[0];
                } else if (hexColors.length > 0) {
                  // Fallback to any hex color
                  bestColor = hexColors[0];
                }

                window.lexicalSelectedColorInfo = {
                  colors: uniqueColors,
                  isMixed: uniqueColors.length > 1,
                  color: bestColor,
                };
              } else {
                window.lexicalSelectedColorInfo = null;
              }
            } catch {
              window.lexicalSelectedFontInfo = null;
              window.lexicalSelectedColorInfo = null;
            }
          } else {
            window.selectedTextInfo = null;
            window.lexicalSelectedFontInfo = null;
            window.lexicalSelectedColorInfo = null;
          }
        });
        // Notify any parent listeners after a small delay to ensure DOM is updated
        setTimeout(() => {
          try {
            window.dispatchEvent(new Event("lexical-selection-change"));
            // Also dispatch editor update event
            window.dispatchEvent(new Event("lexical-editor-update"));
          } catch {}
        }, 10);
      };

      const unregisterListener = editor.registerUpdateListener(() => {
        updateSelection();
      });

      // Also listen to native selection changes for immediate updates
      const handleSelectionChange = () => {
        // Small delay to ensure DOM is updated
        setTimeout(() => {
          updateSelection();
        }, 0);
      };

      document.addEventListener("selectionchange", handleSelectionChange);

      return () => {
        unregisterListener();
        document.removeEventListener("selectionchange", handleSelectionChange);
      };
    }
  }, [editor, style]);

  // Use ref to persist selection across renders
  const savedSelectionRef = useRef(null);

  // Expose formatting functions to parent component
  useEffect(() => {
    if (editor) {
      // Function to save current selection with node content and add visual highlight
      window.saveLexicalSelection = () => {
        // First, save the native browser selection range
        const nativeSelection = window.getSelection();
        const savedNativeRange =
          nativeSelection && nativeSelection.rangeCount > 0
            ? nativeSelection.getRangeAt(0).cloneRange()
            : null;

        editor.getEditorState().read(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection) && !selection.isCollapsed()) {
            // Get all nodes in the selection
            const nodes = selection.getNodes();

            // Filter to only get text nodes that are actually selected
            const selectedTextNodeInfo = [];
            const anchorOffset = selection.anchor.offset;
            const focusOffset = selection.focus.offset;

            nodes.forEach((node) => {
              // Only process text nodes
              if (node.getType() === "text") {
                const nodeKey = node.getKey();
                const nodeText = node.getTextContent();

                // Determine start and end offsets for this specific node
                let startOffset = 0;
                let endOffset = nodeText.length;
                let isPartialSelection = false;

                if (
                  nodeKey === selection.anchor.key &&
                  nodeKey === selection.focus.key
                ) {
                  // Selection is within a single node
                  startOffset = Math.min(anchorOffset, focusOffset);
                  endOffset = Math.max(anchorOffset, focusOffset);
                  // Check if this is a partial selection (not the entire node)
                  isPartialSelection = !(
                    startOffset === 0 && endOffset === nodeText.length
                  );
                } else if (nodeKey === selection.anchor.key) {
                  // This is the anchor node
                  startOffset = selection.isBackward() ? 0 : anchorOffset;
                  endOffset = selection.isBackward()
                    ? anchorOffset
                    : nodeText.length;
                  isPartialSelection =
                    startOffset !== 0 || endOffset !== nodeText.length;
                } else if (nodeKey === selection.focus.key) {
                  // This is the focus node
                  startOffset = selection.isBackward() ? focusOffset : 0;
                  endOffset = selection.isBackward()
                    ? nodeText.length
                    : focusOffset;
                  isPartialSelection =
                    startOffset !== 0 || endOffset !== nodeText.length;
                }

                // Only include nodes that have actual selected content
                if (endOffset > startOffset) {
                  // CRITICAL: Always read the CURRENT highlight state from the node
                  // Don't rely on previous saves - read fresh each time
                  const nodeStyle = node.getStyle() || "";
                  const bgColorMatch = nodeStyle.match(
                    /background-color:\s*([^;]+)/i
                  );
                  let bgColor = bgColorMatch ? bgColorMatch[1].trim() : null;

                  // CRITICAL: Also check the DOM element as a fallback AND sync it back to Lexical
                  // This ensures highlights persist even if they're only in the DOM
                  if (!bgColor) {
                    try {
                      const domElement = editor.getElementByKey(nodeKey);
                      if (domElement) {
                        const domStyle = domElement.getAttribute("style") || "";
                        const domBgMatch = domStyle.match(
                          /background-color:\s*([^;]+)/i
                        );
                        if (domBgMatch) {
                          bgColor = domBgMatch[1].trim();
                          // Sync it back to Lexical node so it persists
                          const currentStyle = node.getStyle() || "";
                          const cleanedStyle = currentStyle
                            .replace(/background-color:\s*[^;]+;?/gi, "")
                            .trim()
                            .replace(/;\s*$/, "")
                            .replace(/^\s*;\s*/, "");
                          const syncedStyle = cleanedStyle
                            ? `${cleanedStyle}; background-color: ${bgColor}`
                            : `background-color: ${bgColor}`;
                          node.setStyle(syncedStyle);
                        }
                      }
                    } catch (e) {
                      // Ignore DOM lookup/sync errors
                    }
                  }

                  const selectedText = nodeText.substring(
                    startOffset,
                    endOffset
                  );
                  selectedTextNodeInfo.push({
                    key: nodeKey,
                    startOffset,
                    endOffset,
                    text: selectedText,
                    fullText: nodeText,
                    isPartial: isPartialSelection,
                    bgColor: bgColor, // Save highlight color for this node (from current state)
                  });

                  if (bgColor) {
                  } else {
                  }
                }
              }
            });

            savedSelectionRef.current = {
              anchorKey: selection.anchor.key,
              anchorOffset: selection.anchor.offset,
              focusKey: selection.focus.key,
              focusOffset: selection.focus.offset,
              selectedText: selection.getTextContent(),
              nodeInfo: selectedTextNodeInfo,
              nativeRange: savedNativeRange,
            };

            const highlightCount = selectedTextNodeInfo.filter(
              (n) => n.bgColor
            ).length;

            // Add visual highlight using CSS - handle full vs partial selections
            setTimeout(() => {
              // Highlight only the selected text node elements
              selectedTextNodeInfo.forEach(
                ({
                  key,
                  startOffset,
                  endOffset,
                  text,
                  fullText,
                  isPartial,
                }) => {
                  const dom = editor.getElementByKey(key);
                  if (dom) {
                    if (
                      !isPartial ||
                      (startOffset === 0 && endOffset === fullText.length)
                    ) {
                      // Full node selection - highlight the entire element
                      dom.style.backgroundColor = "#b4d5fe";
                      dom.setAttribute("data-lexical-selected", "true");
                    } else {
                      // Partial selection - use actual text measurements for accuracy
                      try {
                        const textNode = dom.firstChild;
                        if (textNode && textNode.nodeType === 3) {
                          // Use Range API to measure actual pixel positions
                          const range = document.createRange();

                          // Measure start position
                          range.setStart(textNode, 0);
                          range.setEnd(textNode, startOffset);
                          const startRect = range.getBoundingClientRect();
                          const startX = startRect.width;

                          // Measure end position
                          range.setStart(textNode, 0);
                          range.setEnd(textNode, endOffset);
                          const endRect = range.getBoundingClientRect();
                          const endX = endRect.width;

                          // Get total width
                          range.setStart(textNode, 0);
                          range.setEnd(textNode, fullText.length);
                          const totalRect = range.getBoundingClientRect();
                          const totalWidth = totalRect.width;

                          if (totalWidth > 0) {
                            // Calculate percentages based on actual pixel measurements
                            const startPercent = (startX / totalWidth) * 100;
                            const endPercent = (endX / totalWidth) * 100;

                            const gradient = `linear-gradient(to right, 
                            transparent 0%, 
                            transparent ${startPercent}%, 
                            #b4d5fe ${startPercent}%, 
                            #b4d5fe ${endPercent}%, 
                            transparent ${endPercent}%, 
                            transparent 100%)`;

                            dom.style.backgroundImage = gradient;
                            dom.setAttribute(
                              "data-lexical-selected",
                              "partial"
                            );
                            dom.setAttribute(
                              "data-selection-start",
                              startOffset.toString()
                            );
                            dom.setAttribute(
                              "data-selection-end",
                              endOffset.toString()
                            );
                          } else {
                            // Fallback to character-based percentages if measurements fail
                            const startPercent =
                              (startOffset / fullText.length) * 100;
                            const endPercent =
                              (endOffset / fullText.length) * 100;
                            const gradient = `linear-gradient(to right, 
                            transparent 0%, 
                            transparent ${startPercent}%, 
                            #b4d5fe ${startPercent}%, 
                            #b4d5fe ${endPercent}%, 
                            transparent ${endPercent}%, 
                            transparent 100%)`;
                            dom.style.backgroundImage = gradient;
                            dom.setAttribute(
                              "data-lexical-selected",
                              "partial"
                            );
                          }
                        } else {
                          // No text node found, highlight entire element as fallback
                          dom.style.backgroundColor = "#b4d5fe";
                          dom.setAttribute("data-lexical-selected", "true");
                        }
                      } catch (e) {
                        // Fallback: highlight entire element
                        dom.style.backgroundColor = "#b4d5fe";
                        dom.setAttribute("data-lexical-selected", "true");
                      }
                    }
                  }
                }
              );
            }, 0);
          } else {
            savedSelectionRef.current = null;
          }
        });
      };

      // Function to clear visual highlight
      window.clearLexicalSelectionHighlight = () => {
        // Remove all visual highlights
        const rootElement = editor.getRootElement();
        if (rootElement) {
          // Clear full selections
          const fullHighlighted = rootElement.querySelectorAll(
            '[data-lexical-selected="true"]'
          );
          fullHighlighted.forEach((el) => {
            el.style.backgroundColor = "";
            el.removeAttribute("data-lexical-selected");
          });

          // Clear partial selections (with gradient backgrounds)
          const partialHighlighted = rootElement.querySelectorAll(
            '[data-lexical-selected="partial"]'
          );
          partialHighlighted.forEach((el) => {
            el.style.backgroundImage = "";
            el.removeAttribute("data-lexical-selected");
            el.removeAttribute("data-selection-start");
            el.removeAttribute("data-selection-end");
          });
        }
      };

      // Function to restore saved selection
      window.restoreLexicalSelection = () => {
        if (!savedSelectionRef.current) {
          console.warn("⚠️ No saved selection to restore");
          return;
        }

        // Save highlight formatting BEFORE any operations that might clear it
        // Save both by node key (for immediate restoration) and by text content (for fallback after re-init)
        const savedHighlightInfoByKey = [];
        const savedHighlightInfoByText = [];

        if (savedSelectionRef.current && savedSelectionRef.current.nodeInfo) {
          editor.getEditorState().read(() => {
            savedSelectionRef.current.nodeInfo.forEach(
              ({
                key,
                text,
                startOffset,
                endOffset,
                bgColor: savedBgColor,
              }) => {
                try {
                  // First, try to use the bgColor that was saved during saveLexicalSelection
                  // This is more reliable than reading from the node later
                  if (savedBgColor) {
                    const bgColorLower = savedBgColor.toLowerCase().trim();
                    // Check if it's a yellow highlight
                    if (
                      bgColorLower === "#ffff00" ||
                      bgColorLower === "#ff0" ||
                      bgColorLower === "yellow" ||
                      bgColorLower === "rgb(255, 255, 0)" ||
                      bgColorLower === "rgba(255, 255, 0, 1)"
                    ) {
                      savedHighlightInfoByKey.push({
                        key,
                        bgColor: savedBgColor,
                      });
                      // Also save by text content for fallback
                      if (text) {
                        savedHighlightInfoByText.push({
                          text: text.trim(),
                          bgColor: savedBgColor,
                          startOffset,
                          endOffset,
                        });
                      }
                    }
                  } else {
                    // Fallback: read from node if not saved (for backwards compatibility)
                    const node = $getNodeByKey(key);
                    if (node && node.getType() === "text") {
                      const style = node.getStyle() || "";
                      // Check if node has highlight (yellow background)
                      const bgColorMatch = style.match(
                        /background-color:\s*([^;]+)/i
                      );
                      if (bgColorMatch && bgColorMatch[1]) {
                        const bgColor = bgColorMatch[1].trim().toLowerCase();
                        // Check if it's a yellow highlight
                        if (
                          bgColor === "#ffff00" ||
                          bgColor === "#ff0" ||
                          bgColor === "yellow" ||
                          bgColor === "rgb(255, 255, 0)" ||
                          bgColor === "rgba(255, 255, 0, 1)"
                        ) {
                          savedHighlightInfoByKey.push({
                            key,
                            bgColor: bgColorMatch[1].trim(),
                          });
                          // Also save by text content for fallback
                          if (text) {
                            savedHighlightInfoByText.push({
                              text: text.trim(),
                              bgColor: bgColorMatch[1].trim(),
                              startOffset,
                              endOffset,
                            });
                          }
                        }
                      }
                    }
                  }
                } catch (e) {
                  // Ignore errors - node might not exist anymore
                }
              }
            );
          });
        }

        // Clear visual highlight
        window.clearLexicalSelectionHighlight();

        // First refocus the editor
        const rootElement = editor.getRootElement();
        if (rootElement) {
          rootElement.focus();
        }

        // Then restore the selection in an update
        editor.update(() => {
          try {
            const root = $getRoot();
            const selection = $getSelection();

            if ($isRangeSelection(selection) && savedSelectionRef.current) {
              let selectionRestored = false;

              // Try to restore the exact selection by node keys first
              try {
                selection.anchor.set(
                  savedSelectionRef.current.anchorKey,
                  savedSelectionRef.current.anchorOffset,
                  "text"
                );
                selection.focus.set(
                  savedSelectionRef.current.focusKey,
                  savedSelectionRef.current.focusOffset,
                  "text"
                );
                selectionRestored = true;
              } catch (error) {
                console.warn(
                  "⚠️ Could not restore exact selection, trying alternative:",
                  error
                );
                // If direct restoration fails, try to select using node keys
                try {
                  const anchorNode = $getNodeByKey(
                    savedSelectionRef.current.anchorKey
                  );
                  const focusNode = $getNodeByKey(
                    savedSelectionRef.current.focusKey
                  );
                  if (anchorNode && focusNode) {
                    selection.anchor.set(
                      savedSelectionRef.current.anchorKey,
                      savedSelectionRef.current.anchorOffset,
                      "text"
                    );
                    selection.focus.set(
                      savedSelectionRef.current.focusKey,
                      savedSelectionRef.current.focusOffset,
                      "text"
                    );
                    selectionRestored = true;
                  }
                } catch (e2) {
                  console.warn(
                    "⚠️ Node keys invalid, editor may have re-initialized"
                  );
                }
              }

              // CRITICAL: Restore highlights IMMEDIATELY after restoring selection
              // Do this in the same update cycle to ensure they're applied together
              // Restore highlight formatting - try by node key first
              if (savedHighlightInfoByKey.length > 0) {
                const normalizeColor = (color) => {
                  if (!color) return "";
                  const c = color.toLowerCase().replace(/\s+/g, "");
                  if (c === "#ffff00" || c === "#ff0" || c === "yellow")
                    return "yellow";
                  if (c === "rgb(255,255,0)" || c === "rgba(255,255,0,1)")
                    return "yellow";
                  return c;
                };

                let restoredByKey = 0;
                savedHighlightInfoByKey.forEach(({ key, bgColor }) => {
                  try {
                    const node = $getNodeByKey(key);
                    if (node && node.getType() === "text") {
                      const currentStyle = node.getStyle() || "";
                      const bgColorMatch = currentStyle.match(
                        /background-color:\s*([^;]+)/i
                      );
                      const currentBgColor = bgColorMatch
                        ? bgColorMatch[1].trim().toLowerCase()
                        : "";
                      const savedBgColor = bgColor.trim().toLowerCase();

                      const normalizedCurrent = normalizeColor(currentBgColor);
                      const normalizedSaved = normalizeColor(savedBgColor);

                      // CRITICAL: Always restore highlight if we have a saved one
                      // Apply it unconditionally to ensure it's present
                      if (bgColor && normalizedSaved) {
                        // Always apply the saved highlight - don't check if it's already there
                        // This ensures highlights are restored even if they were cleared
                        // Remove any existing background-color first
                        let cleanedStyle = currentStyle
                          .replace(/background-color:\s*[^;]+;?/gi, "")
                          .trim()
                          .replace(/;\s*$/, "")
                          .replace(/^\s*;\s*/, ""); // Remove leading semicolon

                        // Add the saved highlight
                        const finalStyle = cleanedStyle
                          ? `${cleanedStyle}; background-color: ${bgColor}`
                          : `background-color: ${bgColor}`;

                        node.setStyle(finalStyle);
                        restoredByKey++;

                        // CRITICAL: Also sync to DOM to ensure it's visible and persists
                        // This ensures the highlight is in both Lexical state and DOM
                        try {
                          const domElement = editor.getElementByKey(key);
                          if (domElement) {
                            // Update DOM style to match Lexical style
                            domElement.setAttribute("style", finalStyle);
                          }
                        } catch (e) {
                          // Ignore DOM sync errors
                        }

                        // Verify it was applied to Lexical node
                        const verifyStyle = node.getStyle() || "";
                        const verifyMatch = verifyStyle.match(
                          /background-color:\s*([^;]+)/i
                        );
                      } else {
                      }
                    } else {
                    }
                  } catch (e) {
                    // Node key might be invalid - will try text-based fallback
                  }
                });

                if (restoredByKey === 0 && savedHighlightInfoByKey.length > 0) {
                }
              }

              // Fallback: restore by text content if node keys failed (after a delay to allow re-init)
              // Also use this as a backup even if key-based restoration seemed to work
              if (savedHighlightInfoByText.length > 0) {
                // Use longer setTimeout to ensure editor has fully finished re-initialization
                setTimeout(() => {
                  editor.update(() => {
                    try {
                      const root = $getRoot();
                      const allTextNodes = root.getAllTextNodes();

                      const normalizeColor = (color) => {
                        if (!color) return "";
                        const c = color.toLowerCase().replace(/\s+/g, "");
                        if (c === "#ffff00" || c === "#ff0" || c === "yellow")
                          return "yellow";
                        if (c === "rgb(255,255,0)" || c === "rgba(255,255,0,1)")
                          return "yellow";
                        return c;
                      };

                      savedHighlightInfoByText.forEach(({ text, bgColor }) => {
                        const normalizedSearchText = text.trim();
                        let foundAndRestored = false;

                        // Find nodes containing this text (exact match preferred)
                        allTextNodes.forEach((node) => {
                          const nodeText = node.getTextContent();
                          // Try exact match first, then substring match (trim whitespace for comparison)
                          const normalizedNodeText = nodeText.trim();

                          if (
                            normalizedNodeText === normalizedSearchText ||
                            (normalizedSearchText.length > 0 &&
                              normalizedNodeText.includes(normalizedSearchText))
                          ) {
                            const currentStyle = node.getStyle() || "";
                            const bgColorMatch = currentStyle.match(
                              /background-color:\s*([^;]+)/i
                            );
                            const currentBgColor = bgColorMatch
                              ? bgColorMatch[1].trim().toLowerCase()
                              : "";
                            const savedBgColor = bgColor.trim().toLowerCase();

                            const normalizedCurrent =
                              normalizeColor(currentBgColor);
                            const normalizedSaved =
                              normalizeColor(savedBgColor);

                            // Always apply saved highlight if we have one
                            if (
                              normalizedSaved &&
                              normalizedCurrent !== normalizedSaved
                            ) {
                              const newStyle = currentStyle
                                .replace(/background-color:\s*[^;]+;?/gi, "")
                                .trim()
                                .replace(/;\s*$/, "")
                                .replace(/^\s*;\s*/, "");
                              const finalStyle = newStyle
                                ? `${newStyle}; background-color: ${bgColor}`
                                : `background-color: ${bgColor}`;
                              node.setStyle(finalStyle);
                              foundAndRestored = true;
                            } else if (
                              normalizedCurrent === normalizedSaved &&
                              normalizedSaved
                            ) {
                              foundAndRestored = true; // Already has correct highlight
                            }
                          }
                        });

                        if (!foundAndRestored && bgColor) {
                          console.warn(
                            "⚠️ [RESTORE] Could not find or restore highlight for text:",
                            normalizedSearchText.substring(0, 50)
                          );
                        }
                      });
                    } catch (e) {
                      console.warn(
                        "⚠️ Error in text-based highlight restoration:",
                        e
                      );
                    }
                  });
                }, 100); // Shorter delay since editor shouldn't re-initialize
              }

              // CRITICAL: Final verification pass - check if highlights are present after all restoration attempts
              // This catches cases where highlights were in the HTML but got lost during parsing
              setTimeout(() => {
                if (savedHighlightInfoByText.length > 0) {
                  editor.getEditorState().read(() => {
                    try {
                      const root = $getRoot();
                      const allTextNodes = root.getAllTextNodes();
                      let missingHighlights = 0;

                      const normalizeColor = (color) => {
                        if (!color) return "";
                        const c = color.toLowerCase().replace(/\s+/g, "");
                        if (c === "#ffff00" || c === "#ff0" || c === "yellow")
                          return "yellow";
                        if (c === "rgb(255,255,0)" || c === "rgba(255,255,0,1)")
                          return "yellow";
                        return c;
                      };

                      savedHighlightInfoByText.forEach(({ text, bgColor }) => {
                        const normalizedSearchText = text.trim();
                        let found = false;

                        allTextNodes.forEach((node) => {
                          const nodeText = node.getTextContent().trim();
                          // Match by text content
                          if (
                            nodeText === normalizedSearchText ||
                            (normalizedSearchText.length > 0 &&
                              nodeText.includes(normalizedSearchText))
                          ) {
                            const style = node.getStyle() || "";
                            const bgColorMatch = style.match(
                              /background-color:\s*([^;]+)/i
                            );
                            const currentBgColor = bgColorMatch
                              ? bgColorMatch[1].trim().toLowerCase()
                              : "";

                            const savedNormalized = normalizeColor(bgColor);
                            const currentNormalized =
                              normalizeColor(currentBgColor);

                            if (
                              savedNormalized &&
                              savedNormalized !== currentNormalized
                            ) {
                              // Highlight is missing or wrong, restore it
                              const newStyle = style
                                .replace(/background-color:\s*[^;]+;?/gi, "")
                                .trim()
                                .replace(/;\s*$/, "");
                              const finalStyle = newStyle
                                ? `${newStyle}; background-color: ${bgColor}`
                                : `background-color: ${bgColor}`;
                              node.setStyle(finalStyle);
                              found = true;
                              missingHighlights++;
                            } else if (currentNormalized) {
                              found = true; // Highlight is already present
                            }
                          }
                        });

                        if (!found && bgColor) {
                          console.warn(
                            "⚠️ [RESTORE] Final pass: Could not find node for text:",
                            normalizedSearchText.substring(0, 50)
                          );
                        }
                      });

                      if (missingHighlights > 0) {
                      }
                    } catch (e) {
                      console.warn(
                        "⚠️ Error in final highlight verification:",
                        e
                      );
                    }
                  });
                }
              }, 300); // Final verification pass after all restoration attempts
            }
          } catch (error) {
            console.error("❌ Failed to restore selection:", error);
          }
        });
      };

      // Make formatting functions available globally for the parent component
      window.lexicalEditor = editor;
      window.applyLexicalBold = () => toggleBold(editor);
      window.applyLexicalItalic = () => toggleItalic(editor);
      window.applyLexicalUnderline = () => toggleUnderline(editor);
      window.applyLexicalHighlight = () => toggleHighlight(editor);
      window.applyLexicalTextColor = (color) => applyTextColor(editor, color);
      window.applyLexicalFontSize = (fontSize) =>
        applyFontSize(editor, fontSize);
      window.getLexicalFormattingState = () => getFormattingState(editor);

      // Function that restores selection and applies font size in one operation
      window.applyLexicalFontSizeWithRestore = (fontSize) => {
        if (!savedSelectionRef.current) {
          console.warn("⚠️ No saved selection to restore for font size");
          return;
        }

        // Clear visual highlights first
        window.clearLexicalSelectionHighlight();

        // First refocus the editor
        const rootElement = editor.getRootElement();
        if (rootElement) {
          rootElement.focus();
        }

        // Restore selection and apply font size in the same update
        editor.update(() => {
          try {
            const selection = $getSelection();

            if ($isRangeSelection(selection)) {
              // Restore the selection using the saved anchor and focus points
              selection.anchor.set(
                savedSelectionRef.current.anchorKey,
                savedSelectionRef.current.anchorOffset,
                "text"
              );
              selection.focus.set(
                savedSelectionRef.current.focusKey,
                savedSelectionRef.current.focusOffset,
                "text"
              );

              // Now apply font size to the restored selection
              // Use the saved node info to ensure we only apply to the selected nodes
              const nodeInfo = savedSelectionRef.current.nodeInfo || [];

              nodeInfo.forEach(({ key, startOffset, endOffset }) => {
                const node = $getNodeByKey(key);
                if (node && node.getType() === "text") {
                  const nodeText = node.getTextContent();

                  // If the entire node is selected, just update the style
                  if (startOffset === 0 && endOffset === nodeText.length) {
                    const currentStyle = node.getStyle() || "";
                    const newStyle = currentStyle
                      .replace(/font-size:\s*[^;]+;?/g, "")
                      .trim();
                    const finalStyle = newStyle
                      ? `${newStyle}; font-size: ${fontSize}`
                      : `font-size: ${fontSize}`;
                    node.setStyle(finalStyle);
                  } else {
                    // Partial node selection - properly split the text node

                    const beforeText = nodeText.substring(0, startOffset);
                    const selectedText = nodeText.substring(
                      startOffset,
                      endOffset
                    );
                    const afterText = nodeText.substring(endOffset);

                    // Get the current style for the selected portion
                    const currentStyle = node.getStyle() || "";
                    const newStyle = currentStyle
                      .replace(/font-size:\s*[^;]+;?/g, "")
                      .trim();
                    const finalStyle = newStyle
                      ? `${newStyle}; font-size: ${fontSize}`
                      : `font-size: ${fontSize}`;

                    // Create new text nodes
                    const selectedNode = $createTextNode(selectedText);
                    selectedNode.setStyle(finalStyle);

                    // Apply existing formatting from the original node
                    if (node.hasFormat("bold"))
                      selectedNode.toggleFormat("bold");
                    if (node.hasFormat("italic"))
                      selectedNode.toggleFormat("italic");
                    if (node.hasFormat("underline"))
                      selectedNode.toggleFormat("underline");
                    if (node.hasFormat("highlight"))
                      selectedNode.toggleFormat("highlight");

                    // Split the node and insert the styled portion
                    if (beforeText && afterText) {
                      // Text on both sides - split into 3 nodes
                      const beforeNode = $createTextNode(beforeText);
                      beforeNode.setStyle(currentStyle);
                      if (node.hasFormat("bold"))
                        beforeNode.toggleFormat("bold");
                      if (node.hasFormat("italic"))
                        beforeNode.toggleFormat("italic");
                      if (node.hasFormat("underline"))
                        beforeNode.toggleFormat("underline");
                      if (node.hasFormat("highlight"))
                        beforeNode.toggleFormat("highlight");

                      const afterNode = $createTextNode(afterText);
                      afterNode.setStyle(currentStyle);
                      if (node.hasFormat("bold"))
                        afterNode.toggleFormat("bold");
                      if (node.hasFormat("italic"))
                        afterNode.toggleFormat("italic");
                      if (node.hasFormat("underline"))
                        afterNode.toggleFormat("underline");
                      if (node.hasFormat("highlight"))
                        afterNode.toggleFormat("highlight");

                      node.insertBefore(beforeNode);
                      node.insertBefore(selectedNode);
                      node.insertAfter(afterNode);
                      node.remove();
                    } else if (beforeText) {
                      // Text before - split into 2 nodes
                      const beforeNode = $createTextNode(beforeText);
                      beforeNode.setStyle(currentStyle);
                      if (node.hasFormat("bold"))
                        beforeNode.toggleFormat("bold");
                      if (node.hasFormat("italic"))
                        beforeNode.toggleFormat("italic");
                      if (node.hasFormat("underline"))
                        beforeNode.toggleFormat("underline");
                      if (node.hasFormat("highlight"))
                        beforeNode.toggleFormat("highlight");

                      node.insertBefore(beforeNode);
                      node.insertBefore(selectedNode);
                      node.remove();
                    } else if (afterText) {
                      // Text after - split into 2 nodes
                      const afterNode = $createTextNode(afterText);
                      afterNode.setStyle(currentStyle);
                      if (node.hasFormat("bold"))
                        afterNode.toggleFormat("bold");
                      if (node.hasFormat("italic"))
                        afterNode.toggleFormat("italic");
                      if (node.hasFormat("underline"))
                        afterNode.toggleFormat("underline");
                      if (node.hasFormat("highlight"))
                        afterNode.toggleFormat("highlight");

                      node.insertBefore(selectedNode);
                      node.insertAfter(afterNode);
                      node.remove();
                    } else {
                      // Should not happen, but just in case
                      node.setStyle(finalStyle);
                    }
                  }
                }
              });
            }
          } catch (error) {
            console.error("❌ Failed to restore and apply font size:", error);
          }
        });
      };

      // Enhanced increment/decrement with selection preservation
      window.applyLexicalFontSizeIncrement = () => {
        editor.update(() => {
          // Get default font size from style prop
          const getDefaultFontSize = () => {
            const currentStyle = styleRef.current || {};
            const fontSize = currentStyle.fontSize || "14px";
            const match = String(fontSize).match(/(\d+(?:\.\d+)?)px/);
            return match ? parseInt(match[1], 10) : 14;
          };

          const selection = $getSelection();
          if ($isRangeSelection(selection) && !selection.isCollapsed()) {
            const nodes = selection.getNodes();
            const sizes = new Set();
            const defaultFontSize = getDefaultFontSize();

            nodes.forEach((node) => {
              if (node.getType() === "text") {
                const nodeStyle = node.getStyle() || "";
                const match = nodeStyle.match(/font-size:\s*(\d+)px/);
                if (match) {
                  sizes.add(parseInt(match[1], 10));
                } else {
                  // No explicit font size, use default
                  sizes.add(defaultFontSize);
                }
              }
            });

            // Get max font size from actual text or default
            const maxFontSize =
              sizes.size > 0 ? Math.max(...sizes) : defaultFontSize;
            applyFontSize(editor, `${maxFontSize + 1}px`);
          } else {
            // When no selection, get font size from all text nodes or use global default
            const root = $getRoot();
            const allText = root.getAllTextNodes();
            const sizes = new Set();
            const defaultFontSize = getDefaultFontSize();

            allText.forEach((node) => {
              const nodeStyle = node.getStyle() || "";
              const match = nodeStyle.match(/font-size:\s*(\d+)px/);
              if (match) {
                sizes.add(parseInt(match[1], 10));
              } else {
                sizes.add(defaultFontSize);
              }
            });

            const maxFontSize =
              sizes.size > 0 ? Math.max(...sizes) : defaultFontSize;
            applyFontSize(editor, `${maxFontSize + 1}px`);
          }
        });
      };

      window.applyLexicalFontSizeDecrement = () => {
        editor.update(() => {
          // Get default font size from style prop
          const getDefaultFontSize = () => {
            const currentStyle = styleRef.current || {};
            const fontSize = currentStyle.fontSize || "14px";
            const match = String(fontSize).match(/(\d+(?:\.\d+)?)px/);
            return match ? parseInt(match[1], 10) : 14;
          };

          const selection = $getSelection();
          if ($isRangeSelection(selection) && !selection.isCollapsed()) {
            const nodes = selection.getNodes();
            const sizes = new Set();
            const defaultFontSize = getDefaultFontSize();

            nodes.forEach((node) => {
              if (node.getType() === "text") {
                const nodeStyle = node.getStyle() || "";
                const match = nodeStyle.match(/font-size:\s*(\d+)px/);
                if (match) {
                  sizes.add(parseInt(match[1], 10));
                } else {
                  // No explicit font size, use default
                  sizes.add(defaultFontSize);
                }
              }
            });

            // Get max font size from actual text or default
            const maxFontSize =
              sizes.size > 0 ? Math.max(...sizes) : defaultFontSize;
            applyFontSize(editor, `${Math.max(5, maxFontSize - 1)}px`);
          } else {
            // When no selection, get font size from all text nodes or use global default
            const root = $getRoot();
            const allText = root.getAllTextNodes();
            const sizes = new Set();
            const defaultFontSize = getDefaultFontSize();

            allText.forEach((node) => {
              const nodeStyle = node.getStyle() || "";
              const match = nodeStyle.match(/font-size:\s*(\d+)px/);
              if (match) {
                sizes.add(parseInt(match[1], 10));
              } else {
                sizes.add(defaultFontSize);
              }
            });

            const maxFontSize =
              sizes.size > 0 ? Math.max(...sizes) : defaultFontSize;
            applyFontSize(editor, `${Math.max(5, maxFontSize - 1)}px`);
          }
        });
      };

      // Add function to apply bold and italic together
      window.applyLexicalBoldAndItalic = () => {
        applyBoldAndItalic(editor);
      };

      // Add test function for debugging
      window.testLexicalFormatting = () => {
        // Test applying bold
        toggleBold(editor);

        // Test applying italic
        toggleItalic(editor);
      };
    }

    return () => {
      // Cleanup global functions
      delete window.lexicalEditor;
      delete window.applyLexicalBold;
      delete window.applyLexicalItalic;
      delete window.applyLexicalUnderline;
      delete window.applyLexicalHighlight;
      delete window.applyLexicalTextColor;
      delete window.applyLexicalFontSize;
      delete window.applyLexicalFontSizeWithRestore;
      delete window.applyLexicalFontSizeIncrement;
      delete window.applyLexicalFontSizeDecrement;
      delete window.getLexicalFormattingState;
      delete window.applyLexicalBoldAndItalic;
      delete window.testLexicalFormatting;
      delete window.saveLexicalSelection;
      delete window.restoreLexicalSelection;
      delete window.clearLexicalSelectionHighlight;
    };
  }, [editor]);

  // Autofocus caret into editor (at end) when requested
  useEffect(() => {
    if (!editor || !autoFocus) return;
    // Delay to ensure mount/render complete
    const id = setTimeout(() => {
      try {
        const rootElement = editor.getRootElement();
        if (rootElement) {
          rootElement.focus();
        }
        editor.update(() => {
          try {
            const root = $getRoot();
            const lastChild = root.getLastChild && root.getLastChild();
            if (lastChild && typeof lastChild.selectEnd === "function") {
              lastChild.selectEnd();
              return;
            }
            if (typeof root.selectEnd === "function") {
              root.selectEnd();
              return;
            }
          } catch (_) {
            // Fallback to DOM selection if Lexical APIs unavailable
          }
          const dom = editor.getRootElement();
          if (dom) {
            const range = document.createRange();
            range.selectNodeContents(dom);
            range.collapse(false);
            const sel = window.getSelection();
            if (sel) {
              sel.removeAllRanges();
              sel.addRange(range);
            }
          }
        });
      } catch {}
    }, 0);
    return () => clearTimeout(id);
  }, [editor, autoFocus]);

  return (
    <div style={{ width: "100%" }}>
      {/* CSS Styles for formatting */}
      <style>
        {`
          .editor-content [data-lexical-text="true"] {
            white-space: pre-wrap;
            word-break: break-word;
          }
          .editor-content [data-lexical-text="true"][data-lexical-bold="true"] {
            font-weight: bold;
          }
          .editor-content [data-lexical-text="true"][data-lexical-italic="true"] {
            font-style: italic;
          }
          .editor-content [data-lexical-text="true"][data-lexical-underline="true"] {
            text-decoration: underline;
          }
          .editor-content [data-lexical-text="true"][data-lexical-strikethrough="true"] {
            text-decoration: line-through;
          }
          .editor-content [data-lexical-text="true"][data-lexical-bold="true"][data-lexical-italic="true"] {
            font-weight: bold;
            font-style: italic;
          }
          .editor-content [data-lexical-text="true"][data-lexical-bold="true"][data-lexical-underline="true"] {
            font-weight: bold;
            text-decoration: underline;
          }
          .editor-content [data-lexical-text="true"][data-lexical-italic="true"][data-lexical-underline="true"] {
            font-style: italic;
            text-decoration: underline;
          }
          .editor-content [data-lexical-text="true"][data-lexical-bold="true"][data-lexical-italic="true"][data-lexical-underline="true"] {
            font-weight: bold;
            font-style: italic;
            text-decoration: underline;
          }
          
          /* Additional CSS for better formatting support */
          .editor-content .font-bold {
            font-weight: bold !important;
          }
          .editor-content .italic {
            font-style: italic !important;
          }
          .editor-content .underline {
            text-decoration: underline !important;
          }
          .editor-content .line-through {
            text-decoration: line-through !important;
          }
          
          /* Combined formats */
          .editor-content .font-bold.italic {
            font-weight: bold !important;
            font-style: italic !important;
          }
          .editor-content .font-bold.underline {
            font-weight: bold !important;
            text-decoration: underline !important;
          }
          .editor-content .italic.underline {
            font-style: italic !important;
            text-decoration: underline !important;
          }
          .editor-content .font-bold.italic.underline {
            font-weight: bold !important;
            font-style: italic !important;
            text-decoration: underline !important;
          }
        `}
      </style>
      {/* Lexical Editor */}
      <div
        style={{
          border: "1px solid #dee2e6",
          borderRadius: "4px",
          backgroundColor: "#ffffff",
          overflow: "hidden", // Prevent content from overflowing container
          position: "relative", // Ensure proper positioning for absolute elements
          width: "100%", // Ensure consistent width
          ...style,
        }}
      >
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              style={{
                padding: "8px",
                minHeight: style?.height || "100px", // Use parent height if provided
                maxHeight: style?.height || "200px", // Respect parent height but cap at reasonable max
                outline: "none",
                fontFamily: style?.fontFamily || "Arial, sans-serif",
                fontSize: style?.fontSize || "14px",
                fontWeight: style?.fontWeight || "normal",
                fontStyle: style?.fontStyle || "normal",
                textDecoration: style?.textDecoration || "none",
                color: style?.color || "#000000",
                textAlign: style?.textAlign || "left",
                lineHeight: style?.lineHeight || "1.4",
                overflow: "auto", // Add scroll when content exceeds bounds
                wordWrap: "break-word", // Wrap long words
                whiteSpace: "pre-wrap", // Preserve line breaks but wrap text
                wordBreak: "break-word", // Break long words if needed
                width: "100%", // Ensure it takes full width
                boxSizing: "border-box", // Include padding in width calculation
              }}
              className="editor-content"
              onFocus={onFocus}
              onBlur={(e) => {
                // Call parent onBlur if provided
                if (onBlur) {
                  onBlur(e);
                }
                // Note: We don't clear the selection here to keep it visible
                // even when the editor loses focus
              }}
            />
          }
          placeholder={
            <div
              style={{
                position: "absolute",
                top: "8px",
                left: "8px",
                color: "#6c757d",
                pointerEvents: "none",
                fontFamily: "Arial, sans-serif",
                fontSize: "14px",
              }}
            >
              {placeholder}
            </div>
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <HistoryPlugin />
        <OnChangePlugin onChange={handleEditorChange} />
        <LightTextVisibilityPlugin editorId="SimpleLexicalEditor" />
      </div>
    </div>
  );
}

// Wrapper component that provides Lexical context
export default function SimpleLexicalEditorWrapper(props) {
  const initialConfig = {
    namespace: "SimpleLexicalEditor",
    theme: {
      paragraph: "editor-paragraph",
      text: {
        bold: "font-bold",
        italic: "italic",
        underline: "underline",
        strikethrough: "line-through",
        underlineStrikethrough: "underline line-through",
        code: "rounded bg-gray-100 px-1.5 py-0.5 font-mono text-sm",
      },
    },
    onError: (error) => {},
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <SimpleLexicalEditor {...props} />
    </LexicalComposer>
  );
}
